import { createPatchData } from 'features/fde/helpers/rolodex/createPatchData'
import { useRolodexEdit } from 'features/fde/rolodexAPI'
import { type LibraTicket } from 'features/libra/ticket/libraTicketsApi'
import { generateEntrySlug, useRolodexEntries } from 'features/rolodex'
import isEqual from 'lodash/isEqual'
import { useCallback, useMemo } from 'react'
import {
  type GeckoEntry,
  type RolodexEntryPutJson,
  type RolodexEntryTagsType,
  RolodexEntryType,
} from 'types'
import { v4 } from 'uuid'

import {
  type Comment,
  type CommentAnchor,
  CommentEntryDataSchema,
  type CommentThread,
  type CommentThreadData,
  CommentThreadDataSchema,
  type CommentWithThread,
  type ThreadId,
} from './commentTypes'

function mapEntryToThreadMeta(
  entry: GeckoEntry
): (CommentThreadData & { entryId: ThreadId }) | undefined {
  const parsed = CommentThreadDataSchema.safeParse(entry.data)
  if (!parsed.success) return undefined
  return { ...parsed.data, entryId: entry.id as ThreadId }
}

function mapEntryToComment(entry: GeckoEntry): CommentWithThread | undefined {
  const parsed = CommentEntryDataSchema.safeParse(entry.data)
  if (!parsed.success) return undefined
  const threadId = entry.tags[RolodexEntryType.LIBRA_TICKET_COMMENT_THREAD]
  if (!threadId) return undefined
  return { ...parsed.data, id: entry.id, threadId }
}

const SWR_OPTIONS = {
  compare: isEqual,
  revalidateOnFocus: true,
  refreshInterval: 15_000,
} as const

export interface ThreadMetaEntry extends CommentThreadData {
  entryId: ThreadId
}

export function useTicketThreadEntries(ticketId: string | undefined) {
  const queryBody = useMemo(() => {
    if (!ticketId) return undefined
    return {
      type: RolodexEntryType.LIBRA_TICKET_COMMENT_THREAD,
      tags: { [RolodexEntryType.LIBRA_TICKET]: ticketId },
    }
  }, [ticketId])

  const { entries, isLoading, forceRefresh } = useRolodexEntries(queryBody, {
    swrOptions: SWR_OPTIONS,
  })

  const threadMetas = useMemo<ReadonlyArray<ThreadMetaEntry>>(
    () =>
      (entries ?? [])
        .map(mapEntryToThreadMeta)
        .filter((t): t is ThreadMetaEntry => t !== undefined),
    [entries]
  )

  return useMemo(
    () => ({ threadMetas, isLoading, forceRefresh }),
    [threadMetas, isLoading, forceRefresh]
  )
}

export function useTicketCommentEntries(ticketId: string | undefined) {
  const queryBody = useMemo(() => {
    if (!ticketId) return undefined
    return {
      type: RolodexEntryType.LIBRA_TICKET_COMMENT,
      tags: { [RolodexEntryType.LIBRA_TICKET]: ticketId },
    }
  }, [ticketId])

  const { entries, isLoading, forceRefresh } = useRolodexEntries(queryBody, {
    swrOptions: SWR_OPTIONS,
  })

  const comments = useMemo<ReadonlyArray<CommentWithThread>>(
    () =>
      (entries ?? [])
        .map(mapEntryToComment)
        .filter((c): c is CommentWithThread => c !== undefined),
    [entries]
  )

  return useMemo(
    () => ({ comments, isLoading, forceRefresh }),
    [comments, isLoading, forceRefresh]
  )
}

/**
 * Composes thread entries + comment entries into fully assembled CommentThread
 * objects. Used by CommentsProvider as the raw SWR source before optimistic
 * overlays are applied.
 */
export function useTicketComments(ticketId: string | undefined) {
  const {
    threadMetas,
    isLoading: threadsLoading,
    forceRefresh: refreshThreads,
  } = useTicketThreadEntries(ticketId)

  const {
    comments,
    isLoading: commentsLoading,
    forceRefresh: refreshComments,
  } = useTicketCommentEntries(ticketId)

  const threads = useMemo<ReadonlyArray<CommentThread>>(() => {
    const byThread = new Map<string, Comment[]>()
    for (const c of comments) {
      const list = byThread.get(c.threadId)
      if (list) list.push(c)
      else byThread.set(c.threadId, [c])
    }
    for (const list of byThread.values()) {
      list.sort((a, b) => a.createdAt.localeCompare(b.createdAt))
    }
    return threadMetas.map((meta) => ({
      ...meta,
      comments: byThread.get(meta.entryId as string) ?? [],
    }))
  }, [threadMetas, comments])

  const isLoading = threadsLoading || commentsLoading

  const forceRefresh = useCallback(async () => {
    await Promise.all([refreshThreads(), refreshComments()])
  }, [refreshThreads, refreshComments])

  return useMemo(
    () => ({
      threadMetas,
      comments,
      threads,
      isLoading,
      forceRefresh,
    }),
    [threadMetas, comments, threads, isLoading, forceRefresh]
  )
}

export function buildCommentTags(
  ticket: LibraTicket,
  workflowId: string
): RolodexEntryTagsType {
  return {
    [RolodexEntryType.LIBRA_TICKET]: ticket.id,
    [RolodexEntryType.LIBRA_TICKET_GROUP]:
      ticket.tags[RolodexEntryType.LIBRA_TICKET_GROUP],
    organization: ticket.tags.organization as string,
    site: ticket.tags.site as string,
    unit: ticket.tags.unit as string,
    [RolodexEntryType.LIBRA_WORKFLOW]: workflowId,
  }
}

interface CreateThreadArgs {
  ticket: LibraTicket
  workflowId: string
  anchor: CommentAnchor
  text: string
  authorId: string
  authorName: string
  users: string[]
  valueSnapshot?: unknown
}

export function useCreateCommentThread() {
  const { rolodexPut } = useRolodexEdit()

  return useCallback(
    async ({
      ticket,
      workflowId,
      anchor,
      text,
      authorId,
      authorName,
      users,
      valueSnapshot,
    }: CreateThreadArgs) => {
      const threadId = generateEntrySlug('comment-thread', v4().slice(0, 6))
      const commentId = generateEntrySlug('comment', v4().slice(0, 6))
      const now = new Date().toISOString()
      const baseTags = buildCommentTags(ticket, workflowId)

      await rolodexPut([
        {
          id: threadId,
          type: RolodexEntryType.LIBRA_TICKET_COMMENT_THREAD,
          data: {
            anchor,
            resolved: false,
            users,
            ...(valueSnapshot !== undefined
              ? { valueAtCreation: valueSnapshot }
              : {}),
          },
          tags: baseTags,
        },
        {
          id: commentId,
          type: RolodexEntryType.LIBRA_TICKET_COMMENT,
          data: {
            author: authorId,
            authorName,
            text,
            createdAt: now,
          },
          tags: {
            ...baseTags,
            [RolodexEntryType.LIBRA_TICKET_COMMENT_THREAD]: threadId,
          },
        },
      ])

      return { threadId, commentId }
    },
    [rolodexPut]
  )
}

interface CreateCommentArgs {
  ticket: LibraTicket
  workflowId: string
  threadId: string
  text: string
  authorId: string
  authorName: string
}

export function useCreateComment() {
  const { rolodexPut } = useRolodexEdit()

  return useCallback(
    async ({
      ticket,
      workflowId,
      threadId,
      text,
      authorId,
      authorName,
    }: CreateCommentArgs) => {
      const commentId = generateEntrySlug('comment', v4().slice(0, 6))
      const now = new Date().toISOString()
      const baseTags = buildCommentTags(ticket, workflowId)

      await rolodexPut([
        {
          id: commentId,
          type: RolodexEntryType.LIBRA_TICKET_COMMENT,
          data: {
            author: authorId,
            authorName,
            text,
            createdAt: now,
          },
          tags: {
            ...baseTags,
            [RolodexEntryType.LIBRA_TICKET_COMMENT_THREAD]: threadId,
          },
        },
      ])

      return commentId
    },
    [rolodexPut]
  )
}

export function useResolveThread() {
  const { rolodexPatch } = useRolodexEdit()

  return useCallback(
    async (
      threadEntryId: string,
      currentResolved: boolean,
      userId: string,
      userDisplayName: string
    ) => {
      const patch = createPatchData(
        threadEntryId,
        { resolved: currentResolved },
        {
          resolved: true,
          resolvedBy: userId,
          resolvedByName: userDisplayName,
          resolvedAt: new Date().toISOString(),
        }
      )
      if (patch) {
        await rolodexPatch([patch])
      }
    },
    [rolodexPatch]
  )
}

export function useReopenThread() {
  const { rolodexPatch } = useRolodexEdit()

  return useCallback(
    async (
      threadEntryId: string,
      currentResolved: boolean,
      currentResolvedBy: string | undefined,
      currentResolvedAt: string | undefined
    ) => {
      const patch = createPatchData(
        threadEntryId,
        {
          resolved: currentResolved,
          resolvedBy: currentResolvedBy,
          resolvedAt: currentResolvedAt,
        },
        { resolved: false, resolvedBy: undefined, resolvedAt: undefined }
      )
      if (patch) {
        await rolodexPatch([patch])
      }
    },
    [rolodexPatch]
  )
}

export function useDeleteThread() {
  const { rolodexDelete } = useRolodexEdit()

  return useCallback(
    async (threadId: string, commentIds: string[]) => {
      await Promise.all([
        rolodexDelete(threadId),
        ...commentIds.map((id) => rolodexDelete(id)),
      ])
    },
    [rolodexDelete]
  )
}

export function useDeleteComment() {
  const { rolodexDelete } = useRolodexEdit()

  return useCallback(
    async (commentId: string) => {
      await rolodexDelete(commentId)
    },
    [rolodexDelete]
  )
}

export function useDuplicateActiveThreads(ticketId: string | undefined) {
  const { threadMetas } = useTicketThreadEntries(ticketId)
  const { comments } = useTicketCommentEntries(ticketId)
  const { rolodexPut } = useRolodexEdit()

  return useCallback(
    async (targetTicketId: string, targetTicket: LibraTicket) => {
      const unresolved = threadMetas.filter((t) => !t.resolved)
      if (unresolved.length === 0) return 0

      const unresolvedIds = new Set(unresolved.map((t) => t.entryId as string))
      const targetBaseTags: RolodexEntryTagsType = {
        [RolodexEntryType.LIBRA_TICKET]: targetTicketId,
        [RolodexEntryType.LIBRA_TICKET_GROUP]:
          targetTicket.tags[RolodexEntryType.LIBRA_TICKET_GROUP],
        organization: targetTicket.tags.organization as string,
        site: targetTicket.tags.site as string,
        unit: targetTicket.tags.unit as string,
        [RolodexEntryType.LIBRA_WORKFLOW]: targetTicket.workflowId,
      }

      const putEntries: RolodexEntryPutJson[] = []
      const threadIdMap = new Map<string, string>()

      for (const thread of unresolved) {
        const newThreadId = generateEntrySlug(
          'comment-thread',
          v4().slice(0, 6)
        )
        threadIdMap.set(thread.entryId as string, newThreadId)
        putEntries.push({
          id: newThreadId,
          type: RolodexEntryType.LIBRA_TICKET_COMMENT_THREAD,
          data: {
            anchor: thread.anchor,
            resolved: false,
            users: thread.users,
            ...(thread.valueAtCreation !== undefined
              ? { valueAtCreation: thread.valueAtCreation }
              : {}),
          },
          tags: targetBaseTags,
        })
      }

      for (const comment of comments) {
        if (!unresolvedIds.has(comment.threadId)) continue
        const newThreadId = threadIdMap.get(comment.threadId)
        if (!newThreadId) continue

        const newCommentId = generateEntrySlug('comment', v4().slice(0, 6))
        putEntries.push({
          id: newCommentId,
          type: RolodexEntryType.LIBRA_TICKET_COMMENT,
          data: {
            author: comment.author,
            authorName: comment.authorName,
            text: comment.text,
            createdAt: comment.createdAt,
          },
          tags: {
            ...targetBaseTags,
            [RolodexEntryType.LIBRA_TICKET_COMMENT_THREAD]: newThreadId,
          },
        })
      }

      if (putEntries.length > 0) {
        await rolodexPut(putEntries)
      }
      return unresolved.length
    },
    [threadMetas, comments, rolodexPut]
  )
}

const DASHBOARD_SWR_OPTIONS = {
  compare: isEqual,
  revalidateOnFocus: true,
} as const

/**
 * Unresolved comment threads for a batch of tickets, grouped by ticket id, in
 * a single Rolodex query filtered server-side to `resolved: falsy`. No polling.
 */
export function useUnresolvedThreads(ticketIds: ReadonlyArray<string>): {
  threadsByTicketId: ReadonlyMap<string, ReadonlyArray<ThreadMetaEntry>>
  isLoading: boolean
} {
  const queryBody = useMemo(() => {
    if (ticketIds.length === 0) return undefined
    return {
      type: RolodexEntryType.LIBRA_TICKET_COMMENT_THREAD,
      tags: { [RolodexEntryType.LIBRA_TICKET]: [...ticketIds] },
      filters: [{ key: 'resolved', op: 'falsy' }],
      limit: 10_000,
    }
  }, [ticketIds])

  const { entries, isLoading } = useRolodexEntries(queryBody, {
    swrOptions: DASHBOARD_SWR_OPTIONS,
  })

  const threadsByTicketId = useMemo(() => {
    const map = new Map<string, ThreadMetaEntry[]>()
    for (const entry of entries ?? []) {
      const ticketId = entry.tags[RolodexEntryType.LIBRA_TICKET]
      const thread = mapEntryToThreadMeta(entry)
      if (typeof ticketId === 'string' && thread) {
        const threads = map.get(ticketId) ?? []
        threads.push(thread)
        map.set(ticketId, threads)
      }
    }
    return map
  }, [entries])

  return useMemo(
    () => ({ threadsByTicketId, isLoading }),
    [threadsByTicketId, isLoading]
  )
}

export function useUnresolvedThreadCounts(ticketIds: ReadonlyArray<string>): {
  unresolvedCounts: ReadonlyMap<string, number>
  isLoading: boolean
} {
  const { threadsByTicketId, isLoading } = useUnresolvedThreads(ticketIds)

  const unresolvedCounts = useMemo(
    () =>
      new Map(
        [...threadsByTicketId].map(([ticketId, threads]) => [
          ticketId,
          threads.length,
        ])
      ),
    [threadsByTicketId]
  )

  return useMemo(
    () => ({ unresolvedCounts, isLoading }),
    [unresolvedCounts, isLoading]
  )
}
